import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getChatCompletion } from '../../../services/aiIntegrations/chatCompletion';

const PriceComparisonCheck = ({ selectedVendor, quotes, project, onAddToComparison, projectNeeds = [] }) => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [hasRun, setHasRun] = useState(false);
  const [addedItems, setAddedItems] = useState(new Set());

  // Build a rich product spec string from the selected vendor's quote
  const getProductSpec = () => {
    if (!selectedVendor || !quotes) return null;
    const quote = quotes?.find(q => q?.vendor?.name === selectedVendor?.name);
    if (!quote) return null;
    // Use all specifications joined together for a richer search query
    const specs = quote?.specifications?.filter(Boolean);
    if (!specs?.length) return null;
    return specs?.join(', ');
  };

  // Get the primary product name (first spec line) for display
  const getPrimarySpec = () => {
    if (!selectedVendor || !quotes) return null;
    const quote = quotes?.find(q => q?.vendor?.name === selectedVendor?.name);
    return quote?.specifications?.[0] || null;
  };

  // Build the list of spec fields to extract from projectNeeds
  // Exclude fields that are handled separately (price, delivery, reviews, reputation)
  const NON_SPEC_NEEDS = new Set(['price', 'budget', 'cost', 'delivery', 'deliveryTime', 'shipping', 'reviews', 'reputation', 'vendorRating']);

  const getSpecFieldsFromNeeds = () => {
    if (!projectNeeds?.length) return [];
    return projectNeeds
      ?.filter(need => !NON_SPEC_NEEDS?.has(need?.id))
      ?.map(need => ({ id: need?.id, name: need?.name, description: need?.description }));
  };

  const productSpec = getProductSpec();
  const primarySpec = getPrimarySpec();
  const specFields = getSpecFieldsFromNeeds();

  const runPriceCheck = async () => {
    if (!productSpec) return;

    setLoading(true);
    setError(null);
    setResults(null);

    // Build context-rich search query
    const budgetContext = project?.budget
      ? ` (buyer budget: $${project?.budget?.toLocaleString()})`
      : '';
    const categoryContext = project?.category ? ` [Category: ${project?.category}]` : '';

    // Build dynamic spec extraction instructions from projectNeeds
    const specFieldLines = specFields?.length > 0
      ? specFields
          ?.map(f => `- ${f?.id}: ${f?.name}${f?.description ? ` (${f?.description})` : ''}`)
          ?.join('\n')
      : '- condition: new or refurbished\n- warranty: warranty info';

    // Build the JSON fields list for the format instruction
    const jsonFields = [
      'store',
      'price',
      'url',
      'condition',
      'deal',
      ...(specFields?.length > 0 ? specFields?.map(f => f?.id) : ['warranty'])
    ]?.join(', ');

    const prompt = `Search online retailers right now for the best current prices on: "${productSpec}"${categoryContext}${budgetContext}.

Find the top 5 cheapest listings from different online stores (e.g. Amazon, Best Buy, Walmart, B&H Photo, Costco, Adorama, Newegg, Apple Store, Target, etc.).
For each listing provide:
- Store name
- Exact price in USD
- Direct product URL
- Whether it is new or refurbished
- Any notable deal (sale, coupon, bundle)

Also visit each product page and extract the following specification fields:
${specFieldLines}

If a spec field is not found on the product page, use null for that field.

Format your response as a JSON array with these exact fields: ${jsonFields}
Only return the JSON array, no other text.`;

    try {
      const result = await getChatCompletion(
        'PERPLEXITY',
        'perplexity/sonar-pro',
        [{ role: 'user', content: prompt }],
        {
          temperature: 0.1,
          max_tokens: 2000,
          web_search_options: { search_context_size: 'high' }
        }
      );

      const content = result?.choices?.[0]?.message?.content || '';

      // Strip markdown code fences if present
      let cleanContent = content?.replace(/```json\s*/gi, '')?.replace(/```\s*/g, '')?.trim();

      let parsed = null;

      // Try 1: match a JSON array (greedy — captures full array including nested objects)
      const arrayMatch = cleanContent?.match(/\[\s*\{[\s\S]*\}\s*\]/);
      if (arrayMatch) {
        try { parsed = JSON.parse(arrayMatch?.[0]); } catch (_) {}
      }

      // Try 2: parse the whole cleaned content directly
      if (!parsed) {
        try {
          const direct = JSON.parse(cleanContent);
          parsed = Array.isArray(direct) ? direct : [direct];
        } catch (_) {}
      }

      // Try 3: match a single JSON object and wrap in array
      if (!parsed) {
        const objMatch = cleanContent?.match(/\{[\s\S]*\}/);
        if (objMatch) {
          try {
            const obj = JSON.parse(objMatch?.[0]);
            parsed = Array.isArray(obj) ? obj : [obj];
          } catch (_) {}
        }
      }

      // Try 4: find any JSON-like structure with store/price fields
      if (!parsed) {
        const allObjects = [];
        const objRegex = /\{[^{}]*"store"[^{}]*"price"[^{}]*\}/g;
        let match;
        while ((match = objRegex?.exec(cleanContent)) !== null) {
          try { allObjects?.push(JSON.parse(match?.[0])); } catch (_) {}
        }
        if (allObjects?.length > 0) parsed = allObjects;
      }

      // Try 5: extract price lines from plain text as a last resort
      if (!parsed) {
        const lines = cleanContent?.split('\n')?.filter(Boolean);
        const textItems = [];
        for (const line of lines) {
          const priceMatch = line?.match(/\$([\d,]+(?:\.\d{1,2})?)/);
          if (priceMatch) {
            const storeMatch = line?.match(/^[\d.]*\s*([A-Za-z][\w\s&.'-]{2,30?})/);
            textItems?.push({
              store: storeMatch?.[1]?.trim() || 'Online Retailer',
              price: parseFloat(priceMatch?.[1]?.replace(/,/g, '')),
              url: '',
              condition: 'new',
              deal: line?.trim()?.slice(0, 80)
            });
          }
        }
        if (textItems?.length > 0) parsed = textItems;
      }

      // If still nothing, show the raw response as a single informational item
      if (!parsed || !Array.isArray(parsed) || parsed?.length === 0) {
        if (cleanContent?.length > 10) {
          setResults([{ store: 'Search Result', price: null, url: '', condition: '', deal: cleanContent?.slice(0, 300) }]);
          setLoading(false);
          setHasRun(true);
          return;
        }
        throw new Error('Could not parse price data');
      }

      const sorted = parsed
        ?.filter(item => item?.price !== undefined)
        ?.sort((a, b) => {
          if (a?.price === null) return 1;
          if (b?.price === null) return -1;
          const priceA = typeof a?.price === 'number' ? a?.price : parseFloat(String(a?.price)?.replace(/[^0-9.]/g, ''));
          const priceB = typeof b?.price === 'number' ? b?.price : parseFloat(String(b?.price)?.replace(/[^0-9.]/g, ''));
          return priceA - priceB;
        })
        ?.slice(0, 5);

      setResults(sorted);
    } catch (err) {
      console.error('Price check error:', err);
      setError('FETCH_ERROR');
    } finally {
      setLoading(false);
      setHasRun(true);
    }
  };

  const formatPrice = (price) => {
    if (typeof price === 'number') {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })?.format(price);
    }
    const num = parseFloat(String(price)?.replace(/[^0-9.]/g, ''));
    if (!isNaN(num)) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })?.format(num);
    }
    return String(price);
  };

  const getNumericPrice = (price) => {
    if (typeof price === 'number') return price;
    return parseFloat(String(price)?.replace(/[^0-9.]/g, '')) || Infinity;
  };

  // Get the currently selected vendor's price for comparison
  const selectedQuote = quotes?.find(q => q?.vendor?.name === selectedVendor?.name);
  const selectedPrice = selectedQuote?.price || null;

  if (!selectedVendor || !productSpec) return null;

  return (
    <div className="bg-white rounded-lg border border-orange-200 p-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900 flex items-center">
          <Icon name="ShieldCheck" size={18} className="mr-2 text-orange-500" />
          Price Sanity Check
        </h3>
        {hasRun && results && (
          <button
            onClick={() => { setResults(null); setHasRun(false); }}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            Reset
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Verify you're getting the best deal on{' '}
        <span className="font-medium text-gray-700">{primarySpec}</span> across the web.
      </p>
      {!hasRun && (
        <Button
          variant="outline"
          className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
          iconName="Search"
          iconPosition="left"
          onClick={runPriceCheck}
          disabled={loading}
        >
          {loading ? 'Searching online prices...' : 'Find best online price'}
        </Button>
      )}
      {loading && (
        <div className="flex items-center justify-center py-6 space-x-2">
          <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Scanning online retailers...</span>
        </div>
      )}
      {error === 'FETCH_ERROR' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
          <div className="flex items-start space-x-2">
            <Icon name="AlertCircle" size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-red-800">Could not fetch prices</p>
              <p className="text-xs text-red-600 mt-1">Please try again in a moment.</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="mt-2 text-xs border-red-300 text-red-600 hover:bg-red-50"
            onClick={runPriceCheck}
          >
            Retry
          </Button>
        </div>
      )}
      {results && results?.length > 0 && (
        <div className="space-y-2 mt-2">
          {results?.map((item, index) => {
            const itemPrice = getNumericPrice(item?.price);
            const isCheaper = selectedPrice && itemPrice < selectedPrice;
            const savings = selectedPrice && isCheaper ? selectedPrice - itemPrice : null;
            const isCheapest = index === 0;

            return (
              <div
                key={index}
                className={`rounded-lg border p-3 ${
                  isCheapest
                    ? 'border-green-300 bg-green-50' :'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-1.5">
                      {isCheapest && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                          <Icon name="Trophy" size={10} className="mr-1" />
                          Cheapest
                        </span>
                      )}
                      <span className="text-sm font-medium text-gray-900 truncate">{item?.store}</span>
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`text-base font-bold ${
                        isCheaper ? 'text-green-600' : 'text-gray-800'
                      }`}>
                        {item?.price !== null ? formatPrice(item?.price) : ''}
                      </span>
                      {savings && (
                        <span className="text-xs text-green-600 font-medium">
                          Save {formatPrice(savings)}
                        </span>
                      )}
                    </div>
                    {item?.condition && item?.condition !== 'new' && (
                      <span className="text-xs text-gray-500 capitalize">{item?.condition}</span>
                    )}
                    {item?.deal && (
                      <p className="text-xs text-orange-600 mt-0.5 truncate">{item?.deal}</p>
                    )}
                    {/* Show extracted spec fields inline */}
                    {specFields?.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {specFields?.map(f => item?.[f?.id] ? (
                          <span key={f?.id} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            <span className="font-medium">{f?.name}:</span> {item?.[f?.id]}
                          </span>
                        ) : null)}
                      </div>
                    )}
                  </div>
                  <div className="ml-2 flex-shrink-0 flex flex-col items-end gap-1.5">
                    {item?.url && (
                      <a
                        href={item?.url?.startsWith('http') ? item?.url : `https://${item?.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                        title={`View on ${item?.store}`}
                      >
                        <Icon name="ExternalLink" size={14} />
                      </a>
                    )}
                    {item?.price !== null && (
                      <button
                        onClick={() => {
                          if (onAddToComparison && !addedItems?.has(index)) {
                            onAddToComparison(item);
                            setAddedItems(prev => new Set([...prev, index]));
                          }
                        }}
                        disabled={addedItems?.has(index) || !onAddToComparison}
                        title={!onAddToComparison ? 'Comparison not available' : addedItems?.has(index) ? 'Already added to comparison' : 'Add to side-by-side comparison'}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all ${
                          addedItems?.has(index)
                            ? 'bg-green-100 text-green-700 cursor-default'
                            : !onAddToComparison
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' :'bg-orange-100 text-orange-700 hover:bg-orange-200 cursor-pointer'
                        }`}
                      >
                        <Icon name={addedItems?.has(index) ? 'CheckCircle' : 'PlusCircle'} size={11} />
                        {addedItems?.has(index) ? 'Added ✓' : 'Add'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Summary callout */}
          {selectedPrice && results?.[0] && (
            <div className={`rounded-lg p-3 mt-1 ${
              getNumericPrice(results?.[0]?.price) < selectedPrice
                ? 'bg-green-50 border border-green-200' :'bg-blue-50 border border-blue-200'
            }`}>
              {getNumericPrice(results?.[0]?.price) < selectedPrice ? (
                <div className="flex items-start space-x-2">
                  <Icon name="TrendingDown" size={15} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-green-800">
                    <span className="font-semibold">{results?.[0]?.store}</span> has it for{' '}
                    <span className="font-semibold">{formatPrice(results?.[0]?.price)}</span> —{' '}
                    {formatPrice(selectedPrice - getNumericPrice(results?.[0]?.price))} cheaper than your selected vendor.
                  </p>
                </div>
              ) : (
                <div className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={15} className="text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-800">
                    Your selected vendor offers a <span className="font-semibold">competitive price</span> compared to other online retailers.
                  </p>
                </div>
              )}
            </div>
          )}

          <button
            onClick={runPriceCheck}
            className="text-xs text-gray-400 hover:text-gray-600 mt-1 flex items-center space-x-1"
          >
            <Icon name="RefreshCw" size={11} />
            <span>Refresh prices</span>
          </button>
        </div>
      )}
      {results && results?.length === 0 && (
        <div className="text-center py-4">
          <Icon name="SearchX" size={32} className="text-gray-300 mx-auto mb-2" />
          <p className="text-xs text-gray-500">No price listings found. Try refreshing.</p>
          <button onClick={runPriceCheck} className="text-xs text-blue-500 hover:underline mt-1">Try again</button>
        </div>
      )}
    </div>
  );
};

export default PriceComparisonCheck;
